import React, { use, useEffect, useMemo, useRef, useState } from "react";
import {
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  Grid,
  Typography,
  Snackbar,
  Alert,
  Button,
  FormControlLabel,
  Checkbox,
  OutlinedInput,
} from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useLocation } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { getJobs } from "services/api";
import { getJobDetails } from "services/api";
import LoadingScreen from "components/Loading";
import { getDepartments } from "services/api";
import { getJobGroups } from "services/api";
import { updateJobDetails } from "services/api";
import { Upload } from "@mui/icons-material";
import MDButton from "components/MDButton";
import { exportJD } from "services/api";
import ExportButton from "./components/ExportButton";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import ReactFlow, { Background, Controls, Position } from "reactflow";
import "reactflow/dist/style.css";
import { jobFTEs } from "services/api";
import CustomJobNode from "../CustomJobNode";
import html2canvas from "html2canvas";
import { toPng } from "html-to-image";
import SnackbarWrapper from "components/Snackbar";
import ImportButton from "components/ImportButton";
import { exportJobDetailsExcel } from "services/api";
import { calculateOrgChartLayout } from "services/utils";
import { estimateTextWidth } from "services/utils";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }], // ✅ Added ordered and unordered lists
    [{ align: [] }],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list", // ✅ Required to support ordered/unordered
  "bullet", // ✅ Bullet format
  "align",
];

const JobDetails = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [jobs, setJobs] = useState(location.state?.jobs);
  const [selectedJob, setSelectedJob] = useState(location.state?.jobId);
  const [jobGroups, setJobGroups] = useState([]);
  const [ftes, setFtes] = useState([]);
  const [jobDetails, setJobDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({});
  const [downloadLoading, setDownloadLoading] = useState(false);
  // const [shouldExport, setShouldExport] = useState(false);
  const [isSavingChart, setIsSavingChart] = useState(false);
  const chartRef = useRef(null);
  const snackbarRef = useRef();
  const fromSuccessors = location.state?.fromSuccessors;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobs = async () => {
      if (!location.state?.jobs) {
        try {
          const jobsFromBackend = await getJobs();
          if (jobsFromBackend != "error") setJobs(jobsFromBackend);
        } catch (error) {}
      }
      try {
        const jobGroupsfromBackend = await getJobGroups();
        if (jobGroupsfromBackend != "error") setJobGroups(jobGroupsfromBackend);
      } catch (error) {}
      const ft = await jobFTEs();
      if (ft != "error") setFtes(ft);
    };
    fetchJobs();
  }, [location.state?.jobs]);

  useEffect(() => {
    if (!selectedJob) {
      setJobDetails(null);
      return;
    }
    setLoading(true);
    getJobDetails(selectedJob, i18n.language == "ar" ? "ar" : "en").then((e) => {
      if (e != "error") {
        setJobDetails(e);
      }
      setLoading(false);
    });
  }, [selectedJob]);

  const handleBack = () => {
    navigate(-1);
  };

  // useEffect(() => {
  //   //enters if no organization chart
  //   if (!loading && shouldExport && jobDetails && jobDetails.organizationChart == null) {
  //     // Wait a tick to ensure React Flow renders fully
  //     setTimeout(() => {
  //       captureAndSaveChart();
  //     }, 500); // Delay ensures ReactFlow is ready
  //   }
  // }, [loading, jobDetails,shouldExport]);

  const captureAndSaveChart = async () => {
    if (isSavingChart || !chartRef.current || !jobDetails) {
      return false;
    }

    setIsSavingChart(true);

    let heartbeat;
    try {
      const dataUrl = await Promise.race([
        toPng(chartRef.current, { cacheBust: true, pixelRatio: 2, skipFonts: true }),
        new Promise((_, reject) => setTimeout(() => reject(new Error("TIMEOUT")), 5000)),
      ]);

      clearInterval(heartbeat);

      if (!dataUrl) {
        return false;
      }

      // 1. Extract the Base64 data
      const base64 = dataUrl.split(",")[1];

      const result = await handleFieldSave("Organization Chart", {
        ...jobDetails,
        organizationChart: base64,
      });

      // Return true if the result was successful
      return result === "success" || result === true;
    } catch (err) {
      if (heartbeat) clearInterval(heartbeat);
      return false;
    } finally {
      setIsSavingChart(false);
    }
  };

  const handleFieldSave = async (field, value) => {
    try {
      let res = await updateJobDetails(
        jobDetails.jobDetailId,
        value || jobDetails,
        i18n.language == "ar" ? "ar" : "en"
      );

      if (res != "error") {
        // setJobDetails(value || jobDetails);
        return true; // 🔥 RETURN TRUE
      } else {
        setSaveStatus((prev) => ({ ...prev, [field]: "error" }));
        return false; // 🔥 RETURN FALSE
      }
    } catch (err) {
      setSaveStatus((prev) => ({ ...prev, [field]: "error" }));

      return false; // 🔥 RETURN FALSE
    } finally {
      setTimeout(() => setSaveStatus((prev) => ({ ...prev, [field]: null })), 300);
    }
  };

  // const handleExport = async (lang) => {
  //   setDownloadLoading(true);
  //   await exportJD(selectedJob, jobs?.find((job) => job.jobId === selectedJob)?.jobName, lang);
  //   setDownloadLoading(false);
  // };

  const handleExport = async (lang) => {
    setDownloadLoading(true);
    // setShouldExport(true);

    await new Promise((resolve) => setTimeout(resolve, 1000));

    const success = await captureAndSaveChart();
    if (success) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    const jobName = jobs?.find((job) => job.jobId === selectedJob)?.jobName;
    if (lang === 3) {
      await exportJobDetailsExcel(i18n.language === "ar" ? "ar" : "en");
    } else {
      await exportJD(selectedJob, jobName, lang);
    }

    setDownloadLoading(false);
    // setShouldExport(false); // Reset export trigger
  };

  const handleImportError = (msg, severity) => {
    // Assuming you have a snackbar ref or state
    snackbarRef.current?.showSnackbar(msg, severity);
  };

  const { nodes, edges, nodeTypes } = useMemo(() => {
    if (!selectedJob || !jobs) return { nodes: [], edges: [], nodeTypes: {} };

    // 1. Identify the three layers
    const currentJob = jobs.find((j) => j.jobId === selectedJob);
    if (!currentJob) return { nodes: [], edges: [], nodeTypes: {} };

    const parent = jobs.find((j) => j.jobId === currentJob.reportingToSolidId);
    const children = jobs.filter((j) => j.reportingToSolidId === selectedJob);

    // 2. Configuration for the "Small Margin" layout
    const vSpacing = 160; // Vertical distance between levels
    const hSpacing = 15; // Tight horizontal spacing between children

    // Helper to get width (uses your existing estimate function)
    const getWidth = (job) => Math.max(estimateTextWidth(job.jobName), 140);

    const nodes = [];
    const edges = [];

    // 3. POSITION SELECTED JOB (The Anchor)
    const selectedWidth = getWidth(currentJob);
    const selectedX = 0; // We'll center everything around X=0
    const selectedY = vSpacing;

    nodes.push({
      id: currentJob.jobId.toString(),
      type: "customNode",
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
      position: { x: selectedX, y: selectedY },
      data: {
        label: currentJob.jobName,
        jobGroup:
          jobGroups
            ?.filter((g) => g.jobId === currentJob.jobId)
            .map((g) => g.jobGroup)
            .join(", ") || "",
        fte: ftes?.find(([id]) => id === currentJob.jobId)?.[1] ?? 0,
      },
      width: selectedWidth,
      style: {
        width: `${selectedWidth}px`,
        border: "2px solid #3b82f6",
        borderRadius: "8px",
        backgroundColor: "#fff",
      },
    });

    // 4. POSITION PARENT (1 level up)
    if (parent) {
      const parentWidth = getWidth(parent);
      // Center parent relative to the selected job's center
      const parentX = selectedX + selectedWidth / 2 - parentWidth / 2;
      const parentY = 0;

      nodes.push({
        id: parent.jobId.toString(),
        type: "customNode",
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
        position: { x: parentX, y: parentY },
        data: {
          label: parent.jobName,
          jobGroup:
            jobGroups
              ?.filter((g) => g.jobId === parent.jobId)
              .map((g) => g.jobGroup)
              .join(", ") || "",
          fte: ftes?.find(([id]) => id === parent.jobId)?.[1] ?? 0,
        },
        width: parentWidth,
        style: {
          width: `${parentWidth}px`,
          border: "1px solid #ccc",
          borderRadius: "8px",
          backgroundColor: "#fff",
        },
      });

      // Edge from Parent to Selected
      edges.push({
        id: `e-${parent.jobId}-${currentJob.jobId}`,
        source: parent.jobId.toString(),
        target: currentJob.jobId.toString(),
      });
    }

    // 5. POSITION CHILDREN (1 level down)
    if (children.length > 0) {
      // Calculate total width needed for all children to center them as a group
      const totalChildrenWidth =
        children.reduce((sum, child) => sum + getWidth(child), 0) +
        (children.length - 1) * hSpacing;

      // Start X so that the entire row of children is centered under the selected node
      let currentChildX = selectedX + selectedWidth / 2 - totalChildrenWidth / 2;
      const childrenY = selectedY + vSpacing;

      children.forEach((child) => {
        const childWidth = getWidth(child);

        nodes.push({
          id: child.jobId.toString(),
          type: "customNode",
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
          position: { x: currentChildX, y: childrenY },
          data: {
            label: child.jobName,
            jobGroup:
              jobGroups
                ?.filter((g) => g.jobId === child.jobId)
                .map((g) => g.jobGroup)
                .join(", ") || "",
            fte: ftes?.find(([id]) => id === child.jobId)?.[1] ?? 0,
          },
          width: childWidth,
          style: {
            width: `${childWidth}px`,
            border: "1px solid #ccc",
            borderRadius: "8px",
            backgroundColor: "#fff",
          },
        });

        // Edge from Selected to Child
        edges.push({
          id: `e-${currentJob.jobId}-${child.jobId}`,
          source: currentJob.jobId.toString(),
          target: child.jobId.toString(),
        });

        // Increment X for the next child
        currentChildX += childWidth + hSpacing;
      });
    }

    return {
      nodes,
      edges,
      nodeTypes: { customNode: CustomJobNode },
    };
  }, [jobs, jobGroups, ftes, selectedJob]);

  return (
    <>
      <Button
        onClick={handleBack}
        startIcon={<ArrowBackIcon />}
        sx={{
          mb: 1,
          textTransform: "none",
          fontWeight: 600,
        }}
      >
        {t("common.back")}
      </Button>
      <FormControl fullWidth>
        <Autocomplete
          id="job-autocomplete"
          options={jobs}
          getOptionLabel={(option) => option.jobName || ""}
          value={jobs?.find((job) => job.jobId === selectedJob) || null}
          onChange={(event, newValue) => {
            setSelectedJob(newValue?.jobId || "");
          }}
          disabled={fromSuccessors}
          renderInput={(params) => (
            <TextField {...params} label={t("admin.jobDetails.selectJob")} variant="outlined" />
          )}
        />
      </FormControl>
      {loading ? (
        <LoadingScreen />
      ) : (
        jobDetails && (
          <>
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} mt={2}>
              {/* Export Section */}
              {downloadLoading ? (
                <Box display="flex" alignItems="center" px={2}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <ExportButton handleExport={(lang) => handleExport(lang)} />
              )}

              {/* Import Section */}
              <ImportButton
                allowedTypes={["jobDetails"]}
                buttonText="Import Job Details"
                onError={handleImportError}
                // disabled={!canEdit}
              />
            </Box>
            <Box mt={0}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.roleProfile")}
              </Typography>
              <Grid container spacing={2}>
                {/* Text Fields */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.jobCode")}
                    value={jobDetails.jobCode || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({ ...prev, jobCode: e.target.value }))
                    }
                    onBlur={(e) => handleFieldSave("Job Code")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Job Code"] === "success"
                              ? "green"
                              : saveStatus["Job Code"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Job Code"] === "success"
                              ? "green"
                              : saveStatus["Job Code"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.skillPool")}
                    value={jobDetails.skillPool || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({ ...prev, skillPool: e.target.value }))
                    }
                    onBlur={(e) => handleFieldSave("Skill Pool")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Skill Pool"] === "success"
                              ? "green"
                              : saveStatus["Skill Pool"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Skill Pool"] === "success"
                              ? "green"
                              : saveStatus["Skill Pool"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* TextField */}
                {i18n.language != "ar" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("admin.jobDetails.referenceIndicator")}
                      value={jobDetails.referenceIndicator || ""}
                      onChange={(e) =>
                        setJobDetails((prev) => ({ ...prev, referenceIndicator: e.target.value }))
                      }
                      onBlur={(e) => handleFieldSave("Reference Indicator")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor:
                              saveStatus["Reference Indicator"] === "success"
                                ? "green"
                                : saveStatus["Reference Indicator"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                          "& fieldset": {
                            borderColor:
                              saveStatus["Reference Indicator"] === "success"
                                ? "green"
                                : saveStatus["Reference Indicator"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                        },
                      }}
                    />
                  </Grid>
                )}
                {/* TextField */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.authorizedDeputy")}
                    value={jobDetails.authorizedDeputy || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({ ...prev, authorizedDeputy: e.target.value }))
                    }
                    onBlur={(e) => handleFieldSave("Authorized Deputy")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Authorized Deputy"] === "success"
                              ? "green"
                              : saveStatus["Authorized Deputy"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Authorized Deputy"] === "success"
                              ? "green"
                              : saveStatus["Authorized Deputy"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.documentProperties")}
              </Typography>
              <Grid container spacing={2}>
                {/* Major Amendments */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.documentName")}
                    value={jobDetails.documentName || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({ ...prev, documentName: e.target.value }))
                    }
                    onBlur={() => handleFieldSave("Document Name")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Document Name"] === "success"
                              ? "green"
                              : saveStatus["Document Name"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Document Name"] === "success"
                              ? "green"
                              : saveStatus["Document Name"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Document Prepared By */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.documentPreparedBy")}
                    value={jobDetails.documentPreparedBy || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({ ...prev, documentPreparedBy: e.target.value }))
                    }
                    onBlur={() => handleFieldSave("Document Prepared By")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Document Prepared By"] === "success"
                              ? "green"
                              : saveStatus["Document Prepared By"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Document Prepared By"] === "success"
                              ? "green"
                              : saveStatus["Document Prepared By"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Job Description Modification */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.documentOwner")}
                    value={jobDetails.documentOwner || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        documentOwner: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Document Owner")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Document Owner"] === "success"
                              ? "green"
                              : saveStatus["Document Owner"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Document Owner"] === "success"
                              ? "green"
                              : saveStatus["Document Owner"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.amendmentHistory.title")}
              </Typography>
              <Grid container spacing={2}>
                {/* Major Amendments */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.amendmentHistory.fields.majorAmendments")}
                    value={jobDetails.majorAmendments || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({ ...prev, majorAmendments: e.target.value }))
                    }
                    onBlur={() => handleFieldSave("Major Amendments")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Major Amendments"] === "success"
                              ? "green"
                              : saveStatus["Major Amendments"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Major Amendments"] === "success"
                              ? "green"
                              : saveStatus["Major Amendments"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Commentary */}
                {i18n.language != "ar" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("admin.jobDetails.amendmentHistory.fields.commentary")}
                      value={jobDetails.commentary || ""}
                      onChange={(e) =>
                        setJobDetails((prev) => ({ ...prev, commentary: e.target.value }))
                      }
                      onBlur={() => handleFieldSave("Commentary")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor:
                              saveStatus["Commentary"] === "success"
                                ? "green"
                                : saveStatus["Commentary"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                          "& fieldset": {
                            borderColor:
                              saveStatus["Commentary"] === "success"
                                ? "green"
                                : saveStatus["Commentary"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                        },
                      }}
                    />
                  </Grid>
                )}

                {/* Job Description Modification */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.amendmentHistory.fields.jobDescriptionModification")}
                    value={jobDetails.jobDescriptionModification || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        jobDescriptionModification: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Job Description Modification")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Job Description Modification"] === "success"
                              ? "green"
                              : saveStatus["Job Description Modification"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Job Description Modification"] === "success"
                              ? "green"
                              : saveStatus["Job Description Modification"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Review Date */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.amendmentHistory.fields.reviewDate")}
                    value={jobDetails.reviewDate || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({ ...prev, reviewDate: e.target.value }))
                    }
                    onBlur={() => handleFieldSave("Review Date")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Review Date"] === "success"
                              ? "green"
                              : saveStatus["Review Date"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Review Date"] === "success"
                              ? "green"
                              : saveStatus["Review Date"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>{" "}
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.changesSummary.title")}
              </Typography>
              <Grid container spacing={2}>
                {/* Prior Transformation Phase */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.changesSummary.fields.priorTransformationPhase")}
                    value={jobDetails.priorTransformationPhase || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        priorTransformationPhase: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Prior Transformation Phase")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Prior Transformation Phase"] === "success"
                              ? "green"
                              : saveStatus["Prior Transformation Phase"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Prior Transformation Phase"] === "success"
                              ? "green"
                              : saveStatus["Prior Transformation Phase"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Post Transformation Phase */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.changesSummary.fields.postTransformationPhase")}
                    value={jobDetails.postTransformationPhase || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        postTransformationPhase: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Post Transformation Phase")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Post Transformation Phase"] === "success"
                              ? "green"
                              : saveStatus["Post Transformation Phase"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Post Transformation Phase"] === "success"
                              ? "green"
                              : saveStatus["Post Transformation Phase"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.genericJobData.title")}
              </Typography>
              <Grid container spacing={2}>
                {/* Job Working Schedule */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.genericJobData.fields.jobWorkingSchedule")}
                    value={jobDetails.jobWorkingSchedule || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        jobWorkingSchedule: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Job Working Schedule")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Job Working Schedule"] === "success"
                              ? "green"
                              : saveStatus["Job Working Schedule"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Job Working Schedule"] === "success"
                              ? "green"
                              : saveStatus["Job Working Schedule"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Job Location */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.genericJobData.fields.jobLocation")}
                    value={jobDetails.jobLocation || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        jobLocation: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Job Location")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Job Location"] === "success"
                              ? "green"
                              : saveStatus["Job Location"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Job Location"] === "success"
                              ? "green"
                              : saveStatus["Job Location"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Job Mobility */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.genericJobData.fields.jobMobility")}
                    value={jobDetails.jobMobility || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        jobMobility: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Job Mobility")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Job Mobility"] === "success"
                              ? "green"
                              : saveStatus["Job Mobility"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Job Mobility"] === "success"
                              ? "green"
                              : saveStatus["Job Mobility"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>{" "}
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.organizationChart.title")}
              </Typography>
              <Grid container spacing={2}>
                <Box
                  ref={chartRef}
                  sx={{
                    width: "100%",
                    height: 400,
                    border: "1px solid #ccc",
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    minZoom={0.1}
                    fitView
                  >
                    <Background />
                  </ReactFlow>
                </Box>
              </Grid>
            </Box>
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.jobDimensions.title")}
              </Typography>
              <Grid container spacing={2}>
                {/* TCOE Total Cost of Employment */}
                {i18n.language != "ar" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("admin.jobDetails.jobDimensions.fields.tcoe")}
                      value={jobDetails.tcoe || ""}
                      onChange={(e) => setJobDetails((prev) => ({ ...prev, tcoe: e.target.value }))}
                      onBlur={() => handleFieldSave("TCOE Total Cost of Employment")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor:
                              saveStatus["TCOE Total Cost of Employment"] === "success"
                                ? "green"
                                : saveStatus["TCOE Total Cost of Employment"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                          "& fieldset": {
                            borderColor:
                              saveStatus["TCOE Total Cost of Employment"] === "success"
                                ? "green"
                                : saveStatus["TCOE Total Cost of Employment"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                        },
                      }}
                    />
                  </Grid>
                )}
                {i18n.language != "ar" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("admin.jobDetails.jobDimensions.fields.nationalStaff")}
                      value={jobDetails.numberOfNationalStaff || ""}
                      onChange={(e) =>
                        setJobDetails((prev) => ({
                          ...prev,
                          numberOfNationalStaff: e.target.value,
                        }))
                      }
                      onBlur={() => handleFieldSave("Number of National Staff")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor:
                              saveStatus["Number of National Staff"] === "success"
                                ? "green"
                                : saveStatus["Number of National Staff"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                          "& fieldset": {
                            borderColor:
                              saveStatus["Number of National Staff"] === "success"
                                ? "green"
                                : saveStatus["Number of National Staff"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                        },
                      }}
                    />
                  </Grid>
                )}
                {i18n.language != "ar" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("admin.jobDetails.jobDimensions.fields.expatriateStaff")}
                      value={jobDetails.numberOfExpatriateStaff || ""}
                      onChange={(e) =>
                        setJobDetails((prev) => ({
                          ...prev,
                          numberOfExpatriateStaff: e.target.value,
                        }))
                      }
                      onBlur={() => handleFieldSave("Number of Expatriate Staff")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor:
                              saveStatus["Number of Expatriate Staff"] === "success"
                                ? "green"
                                : saveStatus["Number of Expatriate Staff"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                          "& fieldset": {
                            borderColor:
                              saveStatus["Number of Expatriate Staff"] === "success"
                                ? "green"
                                : saveStatus["Number of Expatriate Staff"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                        },
                      }}
                    />
                  </Grid>
                )}
                {i18n.language != "ar" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("admin.jobDetails.jobDimensions.fields.annualTotalCost")}
                      value={jobDetails.annualTotalCost || ""}
                      onChange={(e) =>
                        setJobDetails((prev) => ({
                          ...prev,
                          annualTotalCost: e.target.value,
                        }))
                      }
                      onBlur={() => handleFieldSave("Annual Total Cost of Employment")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor:
                              saveStatus["Annual Total Cost of Employment"] === "success"
                                ? "green"
                                : saveStatus["Annual Total Cost of Employment"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                          "& fieldset": {
                            borderColor:
                              saveStatus["Annual Total Cost of Employment"] === "success"
                                ? "green"
                                : saveStatus["Annual Total Cost of Employment"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                        },
                      }}
                    />
                  </Grid>
                )}
                {i18n.language == "ar" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("admin.jobDetails.jobDimensions.fields.totalCost")}
                      value={jobDetails.totalCost || ""}
                      onChange={(e) =>
                        setJobDetails((prev) => ({
                          ...prev,
                          totalCost: e.target.value,
                        }))
                      }
                      onBlur={() => handleFieldSave("totalCost")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor:
                              saveStatus["totalCost"] === "success"
                                ? "green"
                                : saveStatus["totalCost"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                          "& fieldset": {
                            borderColor:
                              saveStatus["totalCost"] === "success"
                                ? "green"
                                : saveStatus["totalCost"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                        },
                      }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.jobDimensions.fields.otherKeyStatistics")}
                    value={jobDetails.otherKeyStatistics || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        otherKeyStatistics: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Other Key Statistics Relating to the Job")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Other Key Statistics Relating to the Job"] === "success"
                              ? "green"
                              : saveStatus["Other Key Statistics Relating to the Job"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Other Key Statistics Relating to the Job"] === "success"
                              ? "green"
                              : saveStatus["Other Key Statistics Relating to the Job"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
                {/* Annual Training Budget */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.jobDimensions.fields.annualTrainingBudget")}
                    value={jobDetails.annualTrainingBudget || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        annualTrainingBudget: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Annual Training Budget")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Annual Training Budget"] === "success"
                              ? "green"
                              : saveStatus["Annual Training Budget"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Annual Training Budget"] === "success"
                              ? "green"
                              : saveStatus["Annual Training Budget"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
                {/* JG Job Group Evaluation Date */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.jobDimensions.fields.jgEvaluationDate")}
                    value={jobDetails.jgEvaluationDate || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        jgEvaluationDate: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("JG Job Group Evaluation Date")}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["JG Job Group Evaluation Date"] === "success"
                              ? "green"
                              : saveStatus["JG Job Group Evaluation Date"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["JG Job Group Evaluation Date"] === "success"
                              ? "green"
                              : saveStatus["JG Job Group Evaluation Date"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>{" "}
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.jobPurpose.title")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: "2px solid",
                      borderRadius: 2,
                      backgroundColor: "#fff",
                      overflow: "hidden",
                      borderColor:
                        saveStatus["Job Purpose"] === "success"
                          ? "green"
                          : saveStatus["Job Purpose"] === "error"
                            ? "red"
                            : "#ccc",
                      ".ql-toolbar": {
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        borderBottom: "1px solid #ccc",
                      },
                      ".ql-container": {
                        minHeight: "300px",
                        borderBottomLeftRadius: 8,
                        borderBottomRightRadius: 8,
                        paddingBottom: "16px",
                      },
                    }}
                  >
                    <ReactQuill
                      value={jobDetails.jobPurpose || ""}
                      onChange={(value) =>
                        setJobDetails((prev) => ({
                          ...prev,
                          jobPurpose: value,
                        }))
                      }
                      onBlur={() => handleFieldSave("Job Purpose")}
                      theme="snow"
                      modules={quillModules}
                      formats={quillFormats}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>{" "}
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.keyResponsibilities.title")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: "2px solid",
                      borderRadius: 2,
                      backgroundColor: "#fff",
                      overflow: "hidden",
                      borderColor:
                        saveStatus["Key Responsibilities"] === "success"
                          ? "green"
                          : saveStatus["Key Responsibilities"] === "error"
                            ? "red"
                            : "#ccc",
                      ".ql-toolbar": {
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        borderBottom: "1px solid #ccc",
                      },
                      ".ql-container": {
                        minHeight: "300px",
                        borderBottomLeftRadius: 8,
                        borderBottomRightRadius: 8,
                        paddingBottom: "16px",
                      },
                    }}
                  >
                    <ReactQuill
                      value={jobDetails.keyResponsibilities || ""}
                      onChange={(value) =>
                        setJobDetails((prev) => ({
                          ...prev,
                          keyResponsibilities: value,
                        }))
                      }
                      onBlur={() => handleFieldSave("Key Responsibilities")}
                      theme="snow"
                      modules={quillModules}
                      formats={quillFormats}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>{" "}
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.qualifications.title")}
              </Typography>
              <Grid container spacing={2}>
                {/* Education */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.qualifications.fields.education")}
                    value={jobDetails.education || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        education: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Education")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Education"] === "success"
                              ? "green"
                              : saveStatus["Education"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Education"] === "success"
                              ? "green"
                              : saveStatus["Education"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Experience */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.qualifications.fields.experience")}
                    value={jobDetails.experience || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        experience: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Experience")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Experience"] === "success"
                              ? "green"
                              : saveStatus["Experience"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Experience"] === "success"
                              ? "green"
                              : saveStatus["Experience"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {i18n.language == "ar" && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={t("admin.jobDetails.qualifications.fields.jobValues")}
                      value={jobDetails.jobValues || ""}
                      onChange={(e) =>
                        setJobDetails((prev) => ({
                          ...prev,
                          jobValues: e.target.value,
                        }))
                      }
                      onBlur={() => handleFieldSave("Job Values")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor:
                              saveStatus["Job Values"] === "success"
                                ? "green"
                                : saveStatus["Job Values"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                          "& fieldset": {
                            borderColor:
                              saveStatus["Job Values"] === "success"
                                ? "green"
                                : saveStatus["Job Values"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                        },
                      }}
                    />
                  </Grid>
                )}

                {/* Languages */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.qualifications.fields.languages")}
                    value={jobDetails.languages || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        languages: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Languages")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Languages"] === "success"
                              ? "green"
                              : saveStatus["Languages"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Languages"] === "success"
                              ? "green"
                              : saveStatus["Languages"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Information Technology */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.qualifications.fields.informationTechnology")}
                    value={jobDetails.informationTechnology || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        informationTechnology: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Information Technology")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Information Technology"] === "success"
                              ? "green"
                              : saveStatus["Information Technology"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Information Technology"] === "success"
                              ? "green"
                              : saveStatus["Information Technology"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.workingConditions.title")}
              </Typography>
              <Grid container spacing={2}>
                {/* Physical Requirements */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.workingConditions.fields.physicalRequirements")}
                    value={jobDetails.physicalRequirements || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        physicalRequirements: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Physical Requirements")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Physical Requirements"] === "success"
                              ? "green"
                              : saveStatus["Physical Requirements"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Physical Requirements"] === "success"
                              ? "green"
                              : saveStatus["Physical Requirements"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Mental & Emotional Requirements */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t(
                      "admin.jobDetails.workingConditions.fields.mentalEmotionalRequirements"
                    )}
                    value={jobDetails.mentalEmotionalRequirements || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        mentalEmotionalRequirements: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Mental & Emotional Requirements")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Mental & Emotional Requirements"] === "success"
                              ? "green"
                              : saveStatus["Mental & Emotional Requirements"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Mental & Emotional Requirements"] === "success"
                              ? "green"
                              : saveStatus["Mental & Emotional Requirements"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Equipment */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.workingConditions.fields.equipment")}
                    value={jobDetails.equipment || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        equipment: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Equipment")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Equipment"] === "success"
                              ? "green"
                              : saveStatus["Equipment"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Equipment"] === "success"
                              ? "green"
                              : saveStatus["Equipment"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Indoor */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.workingConditions.fields.indoor")}
                    value={jobDetails.indoor || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        indoor: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Indoor")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Indoor"] === "success"
                              ? "green"
                              : saveStatus["Indoor"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Indoor"] === "success"
                              ? "green"
                              : saveStatus["Indoor"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Outdoor */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.workingConditions.fields.outdoor")}
                    value={jobDetails.outdoor || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        outdoor: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Outdoor")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Outdoor"] === "success"
                              ? "green"
                              : saveStatus["Outdoor"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Outdoor"] === "success"
                              ? "green"
                              : saveStatus["Outdoor"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Working Hazard */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.workingConditions.fields.workingHazard")}
                    value={jobDetails.workingHazard || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        workingHazard: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Working Hazard")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Working Hazard"] === "success"
                              ? "green"
                              : saveStatus["Working Hazard"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Working Hazard"] === "success"
                              ? "green"
                              : saveStatus["Working Hazard"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Working Days (Number Only) */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.workingConditions.fields.workingDays")}
                    value={jobDetails.workingDays || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        workingDays: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Working Days")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Working Days"] === "success"
                              ? "green"
                              : saveStatus["Working Days"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Working Days"] === "success"
                              ? "green"
                              : saveStatus["Working Days"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Days Off (Number Only) */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.workingConditions.fields.daysOff")}
                    value={jobDetails.daysOff || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        daysOff: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Days Off")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Days Off"] === "success"
                              ? "green"
                              : saveStatus["Days Off"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Days Off"] === "success"
                              ? "green"
                              : saveStatus["Days Off"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Working Hours */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.workingConditions.fields.workingHours")}
                    value={jobDetails.workingHours || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        workingHours: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Working Hours")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Working Hours"] === "success"
                              ? "green"
                              : saveStatus["Working Hours"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Working Hours"] === "success"
                              ? "green"
                              : saveStatus["Working Hours"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.workingRelationships.title")}
              </Typography>
              <Grid container spacing={2}>
                {/* Internal Relationships */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.workingRelationships.fields.internalRelationships")}
                    value={jobDetails.internalRelationships || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        internalRelationships: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Internal Relationships")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Internal Relationships"] === "success"
                              ? "green"
                              : saveStatus["Internal Relationships"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Internal Relationships"] === "success"
                              ? "green"
                              : saveStatus["Internal Relationships"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* External Relationships */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.workingRelationships.fields.externalRelationships")}
                    value={jobDetails.externalRelationships || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        externalRelationships: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("External Relationships")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["External Relationships"] === "success"
                              ? "green"
                              : saveStatus["External Relationships"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["External Relationships"] === "success"
                              ? "green"
                              : saveStatus["External Relationships"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            {i18n.language != "ar" && (
              <Box mt={4}>
                <Typography variant="h5" gutterBottom mb={2}>
                  {t("admin.jobDetails.eligibilityStatus.title")}
                </Typography>
                <Grid container spacing={2}>
                  {/* Military Status Dropdown */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="military-status-label">
                        {t("admin.jobDetails.eligibilityStatus.fields.militaryStatus")}
                      </InputLabel>
                      <Select
                        labelId="military-status-label"
                        value={jobDetails.militaryStatus || ""}
                        label={t("admin.jobDetails.eligibilityStatus.fields.militaryStatus")}
                        onChange={(e) => {
                          const value = e.target.value;
                          setJobDetails((prev) => {
                            const updated = { ...prev, militaryStatus: value };
                            handleFieldSave("Military Status", updated);
                            return updated;
                          });
                        }}
                        input={
                          <OutlinedInput
                            label={t("admin.jobDetails.eligibilityStatus.fields.militaryStatus")}
                            sx={{
                              ...fieldStyles,
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor:
                                  saveStatus["Military Status"] === "success"
                                    ? "green"
                                    : saveStatus["Military Status"] === "error"
                                      ? "red"
                                      : undefined,
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor:
                                  saveStatus["Military Status"] === "success"
                                    ? "green"
                                    : saveStatus["Military Status"] === "error"
                                      ? "red"
                                      : undefined,
                              },
                            }}
                          />
                        }
                      >
                        <MenuItem value={t("admin.jobDetails.eligibilityStatus.fields.completed")}>
                          {t("admin.jobDetails.eligibilityStatus.fields.completed")}
                        </MenuItem>
                        <MenuItem value={t("admin.jobDetails.eligibilityStatus.fields.exempted")}>
                          {t("admin.jobDetails.eligibilityStatus.fields.exempted")}
                        </MenuItem>
                        <MenuItem value={t("admin.jobDetails.eligibilityStatus.fields.postponed")}>
                          {t("admin.jobDetails.eligibilityStatus.fields.postponed")}
                        </MenuItem>
                        <MenuItem value={t("admin.jobDetails.eligibilityStatus.fields.inProgress")}>
                          {t("admin.jobDetails.eligibilityStatus.fields.inProgress")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.keyJobChallenges.title")}
              </Typography>
              <Grid container spacing={2}>
                {/* Strengthening Leadership Capabilities */}
                <Grid item xs={12} md={i18n.language === "ar" ? 0 : 4}>
                  <TextField
                    fullWidth
                    label={t(
                      "admin.jobDetails.keyJobChallenges.fields.strengtheningLeadershipCapabilities"
                    )}
                    value={jobDetails.strengtheningLeadershipCapabilities || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        strengtheningLeadershipCapabilities: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Strengthening Leadership Capabilities")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Strengthening Leadership Capabilities"] === "success"
                              ? "green"
                              : saveStatus["Strengthening Leadership Capabilities"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Strengthening Leadership Capabilities"] === "success"
                              ? "green"
                              : saveStatus["Strengthening Leadership Capabilities"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Process Mapping */}
                {i18n.language != "ar" && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={t("admin.jobDetails.keyJobChallenges.fields.processMapping")}
                      value={jobDetails.processMapping || ""}
                      onChange={(e) =>
                        setJobDetails((prev) => ({
                          ...prev,
                          processMapping: e.target.value,
                        }))
                      }
                      onBlur={() => handleFieldSave("Process Mapping")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor:
                              saveStatus["Process Mapping"] === "success"
                                ? "green"
                                : saveStatus["Process Mapping"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                          "& fieldset": {
                            borderColor:
                              saveStatus["Process Mapping"] === "success"
                                ? "green"
                                : saveStatus["Process Mapping"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                        },
                      }}
                    />
                  </Grid>
                )}

                {/* EVP Refinement */}
                {i18n.language != "ar" && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="EVP Refinement"
                      value={jobDetails.evpRefinement || ""}
                      onChange={(e) =>
                        setJobDetails((prev) => ({
                          ...prev,
                          evpRefinement: e.target.value,
                        }))
                      }
                      onBlur={() => handleFieldSave("EVP Refinement")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor:
                              saveStatus["EVP Refinement"] === "success"
                                ? "green"
                                : saveStatus["EVP Refinement"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                          "& fieldset": {
                            borderColor:
                              saveStatus["EVP Refinement"] === "success"
                                ? "green"
                                : saveStatus["EVP Refinement"] === "error"
                                  ? "red"
                                  : undefined,
                          },
                        },
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.topPerformingBehaviours.title")}
              </Typography>
              <Grid container spacing={2}>
                {/* Strengthening Leadership Capabilities */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.topPerformingBehaviours.title")}
                    value={jobDetails.topPerformingBehaviours || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        topPerformingBehaviours: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Top Performing Behaviours")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Top Performing Behaviours"] === "success"
                              ? "green"
                              : saveStatus["Top Performing Behaviours"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Top Performing Behaviours"] === "success"
                              ? "green"
                              : saveStatus["Top Performing Behaviours"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box mt={4}>
              <Typography variant="h5" gutterBottom mb={2}>
                {t("admin.jobDetails.generalTerms.title")}
              </Typography>
              <Grid container spacing={2}>
                {/* Strengthening Leadership Capabilities */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.generalTerms.title")}
                    value={jobDetails.generalTerms || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        generalTerms: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("General Terms & Conditions")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["General Terms & Conditions"] === "success"
                              ? "green"
                              : saveStatus["General Terms & Conditions"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["General Terms & Conditions"] === "success"
                              ? "green"
                              : saveStatus["General Terms & Conditions"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box mt={4}>
              <Grid container spacing={2}>
                {/* Strengthening Leadership Capabilities */}
                <Grid item xs={12} md={4}>
                  <Typography variant="h5" gutterBottom mb={2}>
                    {t("admin.jobDetails.dataIntegrity.title")}
                  </Typography>
                  <TextField
                    fullWidth
                    label={t("admin.jobDetails.dataIntegrity.title")}
                    value={jobDetails.dataIntegrity || ""}
                    onChange={(e) =>
                      setJobDetails((prev) => ({
                        ...prev,
                        dataIntegrity: e.target.value,
                      }))
                    }
                    onBlur={() => handleFieldSave("Data Integrity")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor:
                            saveStatus["Data Integrity"] === "success"
                              ? "green"
                              : saveStatus["Data Integrity"] === "error"
                                ? "red"
                                : undefined,
                        },
                        "& fieldset": {
                          borderColor:
                            saveStatus["Data Integrity"] === "success"
                              ? "green"
                              : saveStatus["Data Integrity"] === "error"
                                ? "red"
                                : undefined,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            <SnackbarWrapper
              ref={snackbarRef}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            />
          </>
        )
      )}
    </>
  );
};

export default JobDetails;
